/**
 * The short announcements a teacher pins to their class, as the API describes
 * them.
 *
 * Mirrors `/institute/notices/*`. Nothing here carries an institute id, an
 * academic year, or who posted it — the server takes all three from the
 * caller's token and the institute's current session.
 */

/** Notice types, as `notices.notice_type` stores them. */
export type NoticeType = 'GENERAL' | 'EVENT' | 'REMINDER' | 'ACADEMIC' | 'IMPORTANT';

/**
 * Finished or not.
 *
 * The column behind it is `publishedAt`, where null is the draft — the server
 * turns that into a word so no screen has to know it was a timestamp.
 */
export type NoticeStatus = 'DRAFT' | 'PUBLISHED';

/** Who inside the class a notice is meant for. */
export type NoticeAudience = 'STUDENTS' | 'PARENTS' | 'BOTH';

/** One notice, as the server returns it. */
export interface TeacherNotice {
  uuid: string;
  title: string;
  /** Empty rather than null, so every screen prints it without branching. */
  body: string;
  noticeType: NoticeType;
  status: NoticeStatus;
  audience: NoticeAudience;
  /** Null for a notice that is not about one subject — a holiday, a uniform check. */
  subjectUuid: string | null;
  subject: string | null;
  /** `YYYY-MM-DD` — the day the notice is *about*, not the day it was written. */
  noticeDate: string;
  /**
   * When it went up, or null while it is a draft.
   *
   * Not `createdAt`: a notice written on Monday and published on Friday went up
   * on Friday, and the card has to say so.
   */
  publishedAt: string | null;
  /** When it was taken down. Set only on the Deleted tab's notices. */
  deletedAt: string | null;
  /** Null when it went to every class the poster takes. */
  classSectionUuid: string | null;
  /** "Class 8 - A", or "All Classes". Built by the server so every screen agrees. */
  targetClass: string;
  postedBy: string;
  /**
   * Whether the caller posted it. Decides whether edit and withdraw are offered;
   * the server still checks ownership on both.
   */
  isMine: boolean;
  /** Full ISO instant, for the time-ago line. */
  createdAt: string;
}

export interface NoticeList {
  notices: TeacherNotice[];
}

/** What the create form sends. `classSectionUuid` absent means all their classes. */
export interface CreateNoticePayload {
  classSectionUuid?: string;
  noticeType: NoticeType;
  subjectUuid?: string;
  audience?: NoticeAudience;
  /** Absent means published — pressing Post is posting it. */
  status?: NoticeStatus;
  title: string;
  body?: string;
  noticeDate: string;
}

/**
 * What an edit sends.
 *
 * Every field optional, and `null` is how one is *cleared* — absent already
 * means "leave it alone", so moving a notice to drafts must not wipe its
 * subject on the way past.
 */
export interface UpdateNoticePayload {
  classSectionUuid?: string | null;
  noticeType?: NoticeType;
  subjectUuid?: string | null;
  audience?: NoticeAudience;
  status?: NoticeStatus;
  title?: string;
  body?: string | null;
  noticeDate?: string;
}

/** What the board's list call may narrow on. All of it is optional. */
export interface NoticeListQuery {
  classSectionUuid?: string;
  subjectUuid?: string;
  /** `DELETED` lists only what has been taken down; absent lists the live board. */
  status?: NoticeStatus | 'DELETED';
  onDate?: string;
}

/** What a notice with no section is called, on screen and in the filters. */
export const ALL_CLASSES = 'All Classes';

/**
 * The three kinds of notice, and how each one is drawn.
 *
 * One home for both boards on purpose: an "Event" must not be one colour when a
 * teacher posts it and another when the office does (CLAUDE.md §3). Read by the
 * student panel, the teacher console and the institute board alike.
 *
 * Written as palette shades rather than as token colours with an opacity
 * modifier. `bg-accent/10` and `border-ok/30` compiled to *nothing* in this
 * Tailwind setup — an opacity modifier on a `var()` colour produces no rule —
 * so General had no tint and no border at all, and Event had no border. Concrete
 * shades work, and carry their own dark variants.
 */
export const NOTICE_TYPES: Record<
  NoticeType,
  { label: string; color: string; bg: string; border: string; dot: string }
> = {
  GENERAL: {
    label: 'General',
    color: 'text-blue-700 dark:text-blue-300',
    bg: 'bg-blue-50 dark:bg-blue-950/50',
    border: 'border-blue-200 dark:border-blue-900/60',
    dot: 'bg-blue-500',
  },
  EVENT: {
    label: 'Event',
    color: 'text-emerald-700 dark:text-emerald-300',
    bg: 'bg-emerald-50 dark:bg-emerald-950/50',
    border: 'border-emerald-200 dark:border-emerald-900/60',
    dot: 'bg-emerald-500',
  },
  REMINDER: {
    label: 'Reminder',
    color: 'text-violet-700 dark:text-violet-300',
    bg: 'bg-violet-50 dark:bg-violet-950/50',
    border: 'border-violet-200 dark:border-violet-900/60',
    dot: 'bg-violet-500',
  },
  ACADEMIC: {
    label: 'Academics',
    color: 'text-sky-700 dark:text-sky-300',
    bg: 'bg-sky-50 dark:bg-sky-950/50',
    border: 'border-sky-200 dark:border-sky-900/60',
    dot: 'bg-sky-500',
  },
  IMPORTANT: {
    label: 'Important',
    color: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-50 dark:bg-amber-950/50',
    border: 'border-amber-200 dark:border-amber-900/60',
    dot: 'bg-amber-500',
  },
};

/** What each audience is called on screen. */
export const NOTICE_AUDIENCE_LABEL: Record<NoticeAudience, string> = {
  STUDENTS: 'All Students',
  PARENTS: 'Parents only',
  BOTH: 'Students and parents',
};

export const NOTICE_PLACEHOLDERS = [
  'Come in color dress tomorrow 🎨',
  'Sports day tomorrow — wear PT kit!',
  'Bring your science project files',
  'Unit test on Monday — Chapter 3 & 4',
  'No school bag required this Friday',
  'Parent-teacher meeting next week',
  'Submit fee receipts by Friday',
  'Library books must be returned today',
];

export function noticeTimeAgo(isoString: string): string {
  const now = Date.now();
  const past = new Date(isoString).getTime();
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
}
